import { createSlice, createSelector } from '@reduxjs/toolkit';

// Initial state cho flow builder
const initialState = {
  // Flow data
  nodes: [],
  edges: [],
  
  // UI state
  selectedNode: null,
  drawerVisible: false,
  paletteCollapsed: false,
  
  // Workflow state
  isExecuting: false,
  executionResult: null,
  showExecutionModal: false,
  
  // Rule state
  currentRule: null,
  loading: false,
  saving: false,
  
  // Global variables cho workflow
  variables: {},
  
  // Workflow history (for undo/redo)
  history: {
    past: [],
    present: { nodes: [], edges: [] },
    future: []
  },
  
  // Performance tracking
  lastModified: null,
  autoSaveEnabled: true,
  nodeIdCounter: 0
};

const adsOptimizationSlice = createSlice({
  name: 'adsOptimization',
  initialState,
  reducers: {
    // Flow data actions
    setNodes: (state, action) => {
      state.nodes = action.payload;
      state.lastModified = new Date().toISOString();
    },

    // Action đặc biệt để load flow ban đầu (có tính toán ID counter)
    loadFlow: (state, action) => {
      const { nodes, edges } = action.payload;
      state.nodes = nodes;
      state.edges = edges;
      state.lastModified = new Date().toISOString();
      
      // Update nodeIdCounter để tránh conflict
      const existingNodeIds = nodes
        .map(node => node.id)
        .filter(id => id.startsWith('node_'))
        .map(id => parseInt(id.replace('node_', '')))
        .filter(id => !isNaN(id));
      
      if (existingNodeIds.length > 0) {
        state.nodeIdCounter = Math.max(...existingNodeIds) + 1;
      }
    },
    
    setEdges: (state, action) => {
      state.edges = action.payload;
      state.lastModified = new Date().toISOString();
    },
    
    addNode: (state, action) => {
      const newNode = {
        ...action.payload,
        id: action.payload.id || `node_${state.nodeIdCounter++}`
      };
      state.nodes.push(newNode);
      state.lastModified = new Date().toISOString();
    },
    
    updateNode: (state, action) => {
      const { nodeId, newData } = action.payload;
      const nodeIndex = state.nodes.findIndex(node => node.id === nodeId);
      
      if (nodeIndex !== -1) {
        state.nodes[nodeIndex] = {
          ...state.nodes[nodeIndex],
          data: newData
        };
        
        // Update selectedNode if it's the same node
        if (state.selectedNode && state.selectedNode.id === nodeId) {
          state.selectedNode = state.nodes[nodeIndex];
        }
        
        state.lastModified = new Date().toISOString();
      }
    },
    
    deleteNode: (state, action) => {
      const nodeId = action.payload;
      state.nodes = state.nodes.filter(node => node.id !== nodeId);
      state.edges = state.edges.filter(edge => edge.source !== nodeId && edge.target !== nodeId);
      
      // Clear selected node if it was deleted
      if (state.selectedNode && state.selectedNode.id === nodeId) {
        state.selectedNode = null;
        state.drawerVisible = false;
      }
      
      state.lastModified = new Date().toISOString();
    },
    
    addEdge: (state, action) => {
      state.edges.push(action.payload);
      state.lastModified = new Date().toISOString();
    },
    
    updateEdges: (state, action) => {
      state.edges = action.payload;
      state.lastModified = new Date().toISOString();
    },
    
    clearFlow: (state) => {
      state.nodes = [];
      state.edges = [];
      state.selectedNode = null;
      state.drawerVisible = false;
      state.lastModified = new Date().toISOString();
    },
    
    // UI state actions
    setSelectedNode: (state, action) => {
      state.selectedNode = action.payload;
    },
    
    setDrawerVisible: (state, action) => {
      state.drawerVisible = action.payload;
      if (!action.payload) {
        state.selectedNode = null;
      }
    },
    
    setPaletteCollapsed: (state, action) => {
      state.paletteCollapsed = action.payload;
    },
    
    // Workflow execution actions
    setExecuting: (state, action) => {
      state.isExecuting = action.payload;
    },
    
    setExecutionResult: (state, action) => {
      state.executionResult = action.payload;
    },
    
    setShowExecutionModal: (state, action) => {
      state.showExecutionModal = action.payload;
    },
    
    // Rule actions
    setCurrentRule: (state, action) => {
      state.currentRule = action.payload;
      
      // Load flow data from rule
      if (action.payload && action.payload.flow) {
        const { nodes: ruleNodes, edges: ruleEdges } = action.payload.flow;
        
        if (ruleNodes && ruleNodes.length > 0) {
          // Update nodeId counter
          const existingNodeIds = ruleNodes
            .map(node => node.id)
            .filter(id => id.startsWith('node_'))
            .map(id => parseInt(id.replace('node_', '')))
            .filter(id => !isNaN(id));
          
          if (existingNodeIds.length > 0) {
            state.nodeIdCounter = Math.max(...existingNodeIds) + 1;
          }
          
          // Decoration node types that should use 'decoration' type in ReactFlow
          const DECORATION_NODE_TYPES = ['comment', 'text_annotation', 'shape_rectangle'];
          
          // Convert rule nodes to ReactFlow format
          state.nodes = ruleNodes.map(node => {
            const nodeType = node.data?.nodeType || node.data?.type || node.type || 'table';
            const isDecoration = DECORATION_NODE_TYPES.includes(nodeType);
            
            // Shape nodes should have lowest zIndex so other nodes appear on top
            const zIndex = nodeType === 'shape_rectangle' ? 0 : (isDecoration ? 1 : 10);
            
            return {
              id: node.id,
              type: isDecoration ? 'decoration' : 'custom',
              position: node.position,
              zIndex: node.zIndex ?? zIndex,
              data: {
                nodeType: nodeType,
                type: node.data?.type || node.data?.nodeType || node.type || 'table',
                config: node.data?.config || node.config || {},
                ...node.data
              }
            };
          });
          
          state.edges = ruleEdges || [];
        } else {
          state.nodes = [];
          state.edges = [];
        }
      } else {
        state.nodes = [];
        state.edges = [];
      }
      
      state.lastModified = new Date().toISOString();
    },
    
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    
    setSaving: (state, action) => {
      state.saving = action.payload;
    },
    
    // Variables actions
    setVariables: (state, action) => {
      state.variables = action.payload;
    },
    
    updateVariable: (state, action) => {
      const { key, value } = action.payload;
      state.variables[key] = value;
    },
    
    clearVariables: (state) => {
      state.variables = {};
    },
    
    // History actions (for undo/redo)
    saveToHistory: (state) => {
      const currentState = { nodes: state.nodes, edges: state.edges };
      state.history.past.push(state.history.present);
      state.history.present = currentState;
      state.history.future = [];
      
      // Limit history size
      if (state.history.past.length > 50) {
        state.history.past = state.history.past.slice(-50);
      }
    },
    
    undo: (state) => {
      if (state.history.past.length > 0) {
        const previous = state.history.past.pop();
        state.history.future.unshift(state.history.present);
        state.history.present = previous;
        state.nodes = previous.nodes;
        state.edges = previous.edges;
        state.lastModified = new Date().toISOString();
      }
    },
    
    redo: (state) => {
      if (state.history.future.length > 0) {
        const next = state.history.future.shift();
        state.history.past.push(state.history.present);
        state.history.present = next;
        state.nodes = next.nodes;
        state.edges = next.edges;
        state.lastModified = new Date().toISOString();
      }
    },
    
    // Bulk actions for performance
    bulkUpdateNodes: (state, action) => {
      const updates = action.payload; // Array of { nodeId, newData }
      updates.forEach(({ nodeId, newData }) => {
        const nodeIndex = state.nodes.findIndex(node => node.id === nodeId);
        if (nodeIndex !== -1) {
          state.nodes[nodeIndex] = {
            ...state.nodes[nodeIndex],
            data: newData
          };
        }
      });
      state.lastModified = new Date().toISOString();
    },
    
    // Auto-save settings
    setAutoSaveEnabled: (state, action) => {
      state.autoSaveEnabled = action.payload;
    },
    
    // Reset state
    resetState: (state) => {
      Object.assign(state, initialState);
    }
  }
});

// Memoized selectors for performance
export const selectNodes = (state) => state.adsOptimization.nodes;
export const selectEdges = (state) => state.adsOptimization.edges;
export const selectSelectedNode = (state) => state.adsOptimization.selectedNode;
export const selectDrawerVisible = (state) => state.adsOptimization.drawerVisible;
export const selectPaletteCollapsed = (state) => state.adsOptimization.paletteCollapsed;
export const selectCurrentRule = (state) => state.adsOptimization.currentRule;
export const selectLoading = (state) => state.adsOptimization.loading;
export const selectSaving = (state) => state.adsOptimization.saving;
export const selectIsExecuting = (state) => state.adsOptimization.isExecuting;
export const selectExecutionResult = (state) => state.adsOptimization.executionResult;
export const selectShowExecutionModal = (state) => state.adsOptimization.showExecutionModal;
export const selectVariables = (state) => state.adsOptimization.variables;
export const selectLastModified = (state) => state.adsOptimization.lastModified;
export const selectAutoSaveEnabled = (state) => state.adsOptimization.autoSaveEnabled;
export const selectNodeIdCounter = (state) => state.adsOptimization.nodeIdCounter;

// Complex selectors with createSelector for memoization
export const selectFlowData = createSelector(
  [selectNodes, selectEdges],
  (nodes, edges) => ({ nodes, edges })
);

export const selectFlowStats = createSelector(
  [selectNodes, selectEdges],
  (nodes, edges) => ({
    nodeCount: nodes.length,
    edgeCount: edges.length,
    hasChanges: nodes.length > 0 || edges.length > 0
  })
);

export const selectNodeById = createSelector(
  [selectNodes, (state, nodeId) => nodeId],
  (nodes, nodeId) => nodes.find(node => node.id === nodeId)
);

export const selectConnectedNodes = createSelector(
  [selectNodes, selectEdges, (state, nodeId) => nodeId],
  (nodes, edges, nodeId) => {
    const connectedEdges = edges.filter(edge => edge.source === nodeId);
    const connectedNodeIds = connectedEdges.map(edge => edge.target);
    return nodes.filter(node => connectedNodeIds.includes(node.id));
  }
);

export const selectCanUndo = (state) => state.adsOptimization.history.past.length > 0;
export const selectCanRedo = (state) => state.adsOptimization.history.future.length > 0;

export const {
  setNodes,
  loadFlow,
  setEdges,
  addNode,
  updateNode,
  deleteNode,
  addEdge,
  updateEdges,
  clearFlow,
  setSelectedNode,
  setDrawerVisible,
  setPaletteCollapsed,
  setExecuting,
  setExecutionResult,
  setShowExecutionModal,
  setCurrentRule,
  setLoading,
  setSaving,
  setVariables,
  updateVariable,
  clearVariables,
  saveToHistory,
  undo,
  redo,
  bulkUpdateNodes,
  setAutoSaveEnabled,
  resetState
} = adsOptimizationSlice.actions;

export default adsOptimizationSlice.reducer;